// Third Party
import * as React from 'react';
import cx from 'classnames';
import { twMerge } from 'tailwind-merge';

// Utils
import { WithCommonProps } from '@types';

export enum SpinnerSize {
  SIZE_SMALL = 24,
  SIZE_LARGE = 48,
}

export type SpinnerStyleProps = {
  /** Size of the spinner */
  size: SpinnerSize;
  /** Color of the spinner */
  color?: string;
};

export type SpinnerProps = WithCommonProps<unknown> &
  Partial<SpinnerStyleProps> & {
    /** Value controlling if it is loading */
    loading?: boolean;
  };

export const Spinner = ({
  loading = true,
  size = SpinnerSize.SIZE_SMALL,
  color,
  className: classNameProp,
  ...otherProps
}: SpinnerProps): React.ReactElement | null => {
  if (!loading) {
    return null;
  }
  const spinnerTailwindClassName = twMerge(
    cx('flex', { 'text-primary-main': !color }),
    classNameProp
  );
  return (
    <div
      className="inline-flex align-items-center justify-content-center"
      {...otherProps}
    >
      <div className={spinnerTailwindClassName} style={{ color }}>
        <SVG size={size} />
      </div>
    </div>
  );
};

const SVG: React.FC<{ size: SpinnerSize }> = ({
  size,
}: {
  size: SpinnerSize;
}) => {
  const staticStyles =
    'flex inline-flex font-normal text-base leading-[18.4px]';
  const varyingStyles = {
    24: 'w-6 h-6',
    48: 'w-12 h-12',
  };

  return (
    <span
      className={`${staticStyles} ${varyingStyles[size]} animate-spinAnimation`}
      role="img"
      aria-hidden="true"
      data-id="ICON-SPINNER"
    >
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        width="100%"
        height="100%"
      >
        <path
          fill="currentColor"
          d="M12.9 3.1c1.3 1.2 2.1 3 2.1 4.9 0 3.9-3.1 7-7 7s-7-3.1-7-7c0-1.9 0.8-3.7 2.1-4.9l-0.8-0.8c-1.4 1.5-2.3 3.5-2.3 5.7 0 4.4 3.6 8 8 8s8-3.6 8-8c0-2.2-0.9-4.2-2.3-5.7l-0.8 0.8z"
        ></path>
      </svg>
    </span>
  );
};
